import { getApps, initializeApp } from 'firebase/app'
import {
  getAnalytics,
  logEvent as logEvents,
  setUserProperties as setFirebaseUserProperties
} from 'firebase/analytics'
import { getPerformance } from 'firebase/performance'
import { getFirestore } from 'firebase/firestore'
import { getMessaging, isSupported } from 'firebase/messaging'
import { fetchAndActivate, getRemoteConfig, getValue } from 'firebase/remote-config'
import { initializeAppCheck, ReCaptchaEnterpriseProvider, getToken } from 'firebase/app-check'
// v9 compat packages are API compatible with v8 code
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import 'firebase/compat/firestore'
import 'firebase/compat/messaging'
import {
  REACT_APP_FIREBASE_API_KEY,
  REACT_APP_FIREBASE_AUTH_DOMAIN,
  REACT_APP_FIREBASE_DATABASE_URL,
  REACT_APP_FIREBASE_PROJECT_ID,
  REACT_APP_FIREBASE_STORAGE_BUCKET,
  REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  REACT_APP_FIREBASE_APP_ID,
  REACT_APP_FIREBASE_MEASUREMENT_ID,
  REACT_APP_AMPLITUDE_KEY,
  REACT_APP_HOST
} from '../constants/config'
import * as amplitude from '@amplitude/analytics-browser'
import { FacebookAuthProvider } from 'firebase/auth'

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: REACT_APP_FIREBASE_API_KEY,
  authDomain: REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: REACT_APP_FIREBASE_DATABASE_URL,
  projectId: REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: REACT_APP_FIREBASE_APP_ID,
  measurementId: REACT_APP_FIREBASE_MEASUREMENT_ID
}

let app = null
let appCheck = null

if (getApps().length === 0) {
  app = initializeApp(firebaseConfig)
  firebase.initializeApp(firebaseConfig)

  // Pass your reCAPTCHA v3 site key (public key) to activate(). Make sure this
  // key is the counterpart to the secret key you set in the Firebase console.
  if (process.env.NODE_ENV !== 'development') {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(
        '6Lc29V8rAAAAAGpQ7SpFdfN__IEQLZWYxgGpu8QW'
      ),

      // Optional argument. If true, the SDK automatically refreshes App Check
      // tokens as needed.
      isTokenAutoRefreshEnabled: true
    })
  }
  // Configure Facebook auth provider
  const facebookProvider = new FacebookAuthProvider()
  facebookProvider.addScope('email')
  facebookProvider.addScope('public_profile')
  facebookProvider.setCustomParameters({
    display: 'popup',
    auth_type: 'rerequest',
    return_scopes: true
  })
} else {
  app = getApps()[0]
  if (process.env.NODE_ENV !== 'development') {
    // Initialize App Check if not already initialized
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(
        '6Lc29V8rAAAAAGpQ7SpFdfN__IEQLZWYxgGpu8QW'
      ),
      isTokenAutoRefreshEnabled: true
    })
  }
}

// ---------------------------------------------------------------------------
// Attach App Check token to outgoing fetch requests targeting ualabee.com
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined' && !window.__fetchWithAppCheckPatched) {
  const originalFetch = window.fetch.bind(window)

  window.fetch = async (input, init = {}) => {
    try {
      const url = typeof input === 'string' ? input : input?.url || ''

      const targetHosts = ['ualabee.com']
      if (REACT_APP_HOST) targetHosts.push(REACT_APP_HOST)

      const shouldAttachToken = targetHosts.some((host) => url.includes(host))

      if (shouldAttachToken) {
        // Ensure we have an App Check instance
        if (appCheck) {
          const { token } = await getToken(appCheck, false)
          const headers = new Headers(init?.headers || {})
          headers.set('X-Firebase-AppCheck', token)
          return originalFetch(input, { ...init, headers })
        }
      }
    } catch (error) {
      // In case of error, fallback to original fetch without modification
      console.error('App Check fetch wrapper error:', error)
    }

    return originalFetch(input, init)
  }

  window.__fetchWithAppCheckPatched = true
}

// Initialize Analytics and get a reference to the service
const analytics = getAnalytics(app)

// Initialize Performance Monitoring and get a reference to the service
const performance = getPerformance(app)

// Initialize Cloud Firestore and get a reference to the service
const db = getFirestore(app)

// Initialize Firebase Cloud Messaging and get a reference to the service
let messaging = null

isSupported()
  .then((canInit) => {
    if (canInit) {
      messaging = getMessaging(app)
    }
  })
  .catch(() => {
    console.info('Messaging not supported')
  })

// Initialize Remote Config and get a reference to the service
const remoteConfig = getRemoteConfig(app)

remoteConfig.settings.minimumFetchIntervalMillis = 3600000

// Variable to track Amplitude initialization
let amplitudeInitialized = false

// Function to initialize Amplitude if enabled
const initializeAmplitudeIfEnabled = async (isEnabled) => {
  if (isEnabled) {
    await amplitude.init(REACT_APP_AMPLITUDE_KEY, {
      autocapture: true, // Use autocapture instead of defaultTracking
      minIdLength: 1, // Override minimum ID length
      transport: 'xhr' // Use XMLHttpRequest for network requests
    }).promise

    amplitudeInitialized = true
  }
}

// Setup function that also checks Amplitude initialization
const setupRemoteConfigAndInitializeAmplitude = async () => {
  await fetchAndActivate(remoteConfig)
  const enableAmplitudeValue =
    getValue(remoteConfig, 'amplitude_analytics_enabled')._value || 'true'
  const isEnabled = enableAmplitudeValue.toLowerCase() === 'true'
  await initializeAmplitudeIfEnabled(isEnabled)
  return isEnabled
}

// Initialize Amplitude
setupRemoteConfigAndInitializeAmplitude()

// Functions to set user properties and log events
const setUserProperties = (props) => {
  setFirebaseUserProperties(analytics, props)
  if (amplitudeInitialized) {
    const identifyEvent = new amplitude.Identify()
    Object.keys(props).forEach((key) => {
      identifyEvent.set(key, props[key])
    })
    amplitude.identify(identifyEvent)
  }
}

const logEvent = (eventName, params) => {
  const isIosApp =
    navigator.userAgent.includes('UalabeeLite=true') ||
    window.location.search.includes('UalabeeLite=true')

  const eventParams = {
    ...params,
    os: isIosApp ? 'ios' : 'web'
  }

  logEvents(analytics, eventName, eventParams)
  if (amplitudeInitialized) {
    amplitude.track(eventName, eventParams)
  }
}

export {
  analytics,
  db,
  setUserProperties,
  logEvent,
  performance,
  messaging,
  remoteConfig,
  app,
  appCheck
}
